import { ANNEX_III, BASE_CONTROLS, CONTROLS } from "./library.js";
import { ROLE_FACETS } from "./types.js";
/**
 * Applicability engine: maps a system profile to the obligations that apply
 * in each selected jurisdiction, and each obligation to common controls.
 * Pure functions only, so the API and the web app compute identical results.
 */
export function obligations(s) {
    const J = s.jurisdictions;
    const f = s.flags ?? {};
    // EU: building and putting into service under your own name makes you a provider, even for own use.
    const { builds: prov, uses: dep } = ROLE_FACETS[s.role];
    const annexIII = ANNEX_III.includes(s.domain);
    const people = !!f.people || annexIII;
    const out = [];
    const add = (o) => out.push(o);
    if (J.includes("EU")) {
        add({ id: "eu-literacy", jurisdiction: "EU", title: "AI literacy", basis: "Art 4", appliesFrom: "2025-02-02", controls: ["C04"], confidence: "M", source: "omnibus",
            detail: "Applies to providers and deployers. The Omnibus softened this duty; confirm the final wording in Regulation (EU) 2026/1744." });
        add({ id: "eu-prohibited", jurisdiction: "EU", title: "Prohibited practices screen", basis: "Art 5", appliesFrom: "2025-02-02", controls: ["C03", "C06"], confidence: "M", source: "omnibusGD",
            detail: "Confirm the system is not a prohibited practice. The Omnibus added a prohibition on non-consensual intimate imagery and CSAM generators; check its start date." });
        if (f.gpai && prov)
            add({ id: "eu-gpai", jurisdiction: "EU", title: "General-purpose AI model obligations", basis: "Arts 53 to 55", appliesFrom: "2025-08-02", controls: ["C10", "C11", "C06"], confidence: "S", source: "verify",
                detail: "Documentation, copyright policy and training-content summary; extra duties above the systemic-risk threshold. Unchanged by the Omnibus." });
        if (f.chatbot)
            add({ id: "eu-interaction", jurisdiction: "EU", title: "AI interaction disclosure", basis: "Art 50", appliesFrom: "2026-08-02", controls: ["C11"], confidence: "S", source: "omnibus",
                detail: "People must be told they are interacting with an AI system unless obvious. Not deferred by the Omnibus." });
        if (f.synthetic && prov)
            add({ id: "eu-marking", jurisdiction: "EU", title: "Machine-readable marking of synthetic content", basis: "Art 50(2)", appliesFrom: "2026-12-02", controls: ["C11", "C13"], confidence: "S", source: "hlc",
                detail: "Deferred to 2 December 2026 under the provisional agreement. Confirm against the enacted text." });
        if (f.synthetic && dep)
            add({ id: "eu-deepfake", jurisdiction: "EU", title: "Deepfake and generated-text disclosure", basis: "Art 50(4)", appliesFrom: "2026-08-02", controls: ["C11"], confidence: "M", source: "omnibus",
                detail: "Deployers disclose deepfakes and AI-generated text published on matters of public interest." });
        if (annexIII) {
            if (prov)
                add({ id: "eu-hr-provider", jurisdiction: "EU", title: "High-risk provider obligations (Annex III)", basis: "Arts 9 to 17, 43, 49, 72, 73", appliesFrom: "2027-12-02",
                    controls: ["C06", "C07", "C09", "C10", "C11", "C12", "C13", "C14", "C15"], confidence: "S", source: "omnibus",
                    detail: "Risk management, data governance, documentation, logging, oversight, accuracy, QMS, conformity assessment, registration, post-market monitoring." });
            if (dep)
                add({ id: "eu-hr-deployer", jurisdiction: "EU", title: "High-risk deployer obligations (Annex III)", basis: "Art 26", appliesFrom: "2027-12-02",
                    controls: ["C02", "C04", "C12", "C14", "C15", "C10"], confidence: "S", source: "omnibus",
                    detail: "Use per instructions, assign competent oversight, monitor, keep logs, inform workers and affected people." });
            if (dep && (f.publicBody || s.domain === "essential"))
                add({ id: "eu-fria", jurisdiction: "EU", title: "Fundamental rights impact assessment", basis: "Art 27", appliesFrom: "2027-12-02",
                    controls: ["C08"], confidence: "M", source: "omnibus",
                    detail: "Required for public bodies and some private deployers, including credit scoring and life or health insurance pricing. Check scope." });
        }
        if (s.domain === "product")
            add({ id: "eu-annex-i", jurisdiction: "EU", title: "High-risk obligations (Annex I products)", basis: "Art 6(1); Annex I", appliesFrom: "2028-08-02",
                controls: ["C06", "C07", "C10", "C13", "C14"], confidence: "S", source: "omnibusGD",
                detail: "AI as a safety component of products under EU harmonisation law. Final text narrowed machinery scope; check sector legislation." });
    }
    if (J.includes("UK")) {
        add({ id: "uk-principles", jurisdiction: "UK", title: "Cross-sector principles via regulators", basis: "AI White Paper principles", appliesFrom: null, kind: "Regulator expectation",
            controls: ["C06", "C11", "C18", "C02", "C17"], confidence: "S", source: "ukOC",
            detail: "Safety, transparency, fairness, accountability, contestability, applied by the relevant sector regulator. No horizontal AI statute." });
        if (f.personal)
            add({ id: "uk-dp", jurisdiction: "UK", title: "Data protection and automated decisions", basis: "UK GDPR; DPA 2018; Data (Use and Access) Act 2025", appliesFrom: "in-force",
                controls: ["C08", "C09", "C11", "C17"], confidence: "M", source: "ukOC",
                detail: "DPIA, lawful basis and safeguards for significant automated decisions. Check DUAA commencement for the ADM changes." });
        if (people)
            add({ id: "uk-equality", jurisdiction: "UK", title: "Equality duties", basis: "Equality Act 2010", appliesFrom: "in-force", controls: ["C18", "C08"], confidence: "M", source: "ukOC",
                detail: "Indirect discrimination and, for public bodies, the public sector equality duty." });
        if (f.fs)
            add({ id: "uk-fs", jurisdiction: "UK", title: "Financial services conduct and accountability", basis: "FCA Consumer Duty; SM&CR", appliesFrom: "in-force",
                controls: ["C02", "C17", "C13", "C14"], confidence: "M", source: "ukOC",
                detail: "Good customer outcomes and a named senior manager accountable for AI use." });
        if (f.chatbot)
            add({ id: "uk-osa", jurisdiction: "UK", title: "Online safety scope check", basis: "Online Safety Act 2023", appliesFrom: "in-force", controls: ["C06", "C11"], confidence: "S", source: "ukBB",
                detail: "Applies to some chatbots on user-to-user and search services; the Crime and Policing Act 2026 gives powers to extend reach to more chatbots." });
        add({ id: "uk-growth-bill", jurisdiction: "UK", title: "Regulating for Growth Bill and AI sandboxes", basis: "King's Speech 2026", appliesFrom: null, kind: "Watch", controls: [], confidence: "S", source: "ukHansard",
            detail: "Cross-economy sandbox powers. Bill text awaited." });
    }
    if (J.includes("AU")) {
        add({ id: "au-ai6", jurisdiction: "AU", title: "Guidance for AI Adoption (AI6)", basis: "NAIC, Oct 2025", appliesFrom: null, kind: "Voluntary",
            controls: ["C02", "C08", "C06", "C11", "C13", "C12"], confidence: "S", source: "auAI6",
            detail: "Six practices: accountability, impacts, risk, information sharing, testing and monitoring, human control." });
        if (f.personal)
            add({ id: "au-privacy", jurisdiction: "AU", title: "Privacy Act and APPs", basis: "Privacy Act 1988", appliesFrom: "in-force", controls: ["C09", "C11", "C08"], confidence: "S", source: "auRiver",
                detail: "Applies to personal information put into AI tools and generated by them." });
        if (people)
            add({ id: "au-discrim", jurisdiction: "AU", title: "Anti-discrimination law", basis: "Federal and state acts", appliesFrom: "in-force", controls: ["C18"], confidence: "M", source: "auRiver",
                detail: "Technology-neutral; applies to AI-assisted decisions." });
        if (f.chatbot || f.synthetic)
            add({ id: "au-acl", jurisdiction: "AU", title: "Misleading conduct", basis: "Australian Consumer Law", appliesFrom: "in-force", controls: ["C11", "C13"], confidence: "M", source: "auRiver",
                detail: "Outputs and claims about AI must not mislead consumers." });
        if (f.auGov)
            add({ id: "au-cth", jurisdiction: "AU", title: "Commonwealth AI policy, mandatory requirements", basis: "DTA policy for the responsible use of AI in government (updated Dec 2025)", appliesFrom: "2026-12-10",
                controls: ["C02", "C03", "C04", "C08", "C11", "C16"], confidence: "M", source: "auDta",
                detail: "First new requirement from 15 June 2026; the rest in December 2026: AI use-case register, impact assessment before deployment, foundational training, incident process. The DTA gives the month only; 10 December comes from secondary sources." });
        add({ id: "au-standards", jurisdiction: "AU", title: "Australian Standards for AI", basis: "Office of AI, PM&C", appliesFrom: null, kind: "Watch", controls: [], confidence: "S", source: "auBM",
            detail: "Announced 15 July 2026. Enabling legislation planned for early 2027; standards still being designed." });
    }
    if (J.includes("US"))
        usObligations(s, people).forEach(add);
    return out;
}
/**
 * United States. There is no federal AI statute: federal obligations come from
 * existing law, and AI-specific duties are state law, so most items depend on
 * the states the system is used in.
 */
function usObligations(s, people) {
    const f = s.flags ?? {};
    const st = new Set(s.usStates ?? []);
    const { builds: prov, uses: dep, supplies } = ROLE_FACETS[s.role];
    const employment = s.domain === "employment";
    // Areas Colorado SB 26-189 treats as consequential decisions.
    const coConsequential = ["employment", "essential", "education"].includes(s.domain) || !!f.healthcare;
    const out = [];
    const add = (o) => out.push(o);
    // Federal
    add({ id: "us-ftc", jurisdiction: "US", title: "Unfair or deceptive practices", basis: "FTC Act s5", appliesFrom: "in-force",
        controls: ["C11", "C13", "C06"], confidence: "M", source: "usFederal",
        detail: "Claims about what the AI does, undisclosed AI use that misleads, and unsafe outputs can all be unfair or deceptive. No AI-specific federal statute; existing law applies." });
    if (people)
        add({ id: "us-civil-rights", jurisdiction: "US", title: "Federal anti-discrimination law", basis: "Title VII; ADA; ADEA; Fair Housing Act", appliesFrom: "in-force",
            controls: ["C18", "C08", "C17"], confidence: "M", source: "usFederal",
            detail: "Disparate impact from an AI-assisted decision is the deployer's liability, including when a vendor built the tool." });
    if (s.domain === "essential" && f.fs && dep)
        add({ id: "us-credit", jurisdiction: "US", title: "Adverse action notices in credit", basis: "ECOA and Regulation B; FCRA", appliesFrom: "in-force",
            controls: ["C11", "C17", "C10"], confidence: "M", source: "usFederal",
            detail: "Applicants refused credit are owed the specific principal reasons, which the model must be able to support." });
    if (f.usFed)
        add({ id: "us-omb", jurisdiction: "US", title: "Minimum practices for high-impact AI in federal agencies", basis: "OMB M-25-21", appliesFrom: "2026-04-03",
            controls: ["C08", "C13", "C14", "C04", "C12", "C17"], confidence: "S", source: "usOmb",
            detail: "Pre-deployment testing, impact assessment, monitoring, operator training, fail-safes, appeal and end-user feedback for high-impact use cases." });
    add({ id: "us-nist", jurisdiction: "US", title: "NIST AI Risk Management Framework", basis: "NIST AI 100-1; AI 600-1", appliesFrom: null, kind: "Voluntary",
        controls: ["C05", "C06", "C07", "C14"], confidence: "S", source: "nist",
        detail: "The reference US framework. Regulators and some state laws point to it." });
    add({ id: "us-preemption", jurisdiction: "US", title: "Federal preemption of state AI laws", basis: "EO 14365; draft federal bill", appliesFrom: null, kind: "Watch",
        controls: [], confidence: "S", source: "usPreemption",
        detail: "The administration is pressing to override state AI laws. No state AI law has been enjoined and no preemption bill has passed, so state laws apply." });
    // California
    if (st.has("CA")) {
        if (f.frontier && prov)
            add({ id: "ca-tfaia", jurisdiction: "US", title: "California frontier AI transparency", basis: "CA SB 53 (TFAIA)", appliesFrom: "2026-01-01",
                controls: ["C06", "C10", "C11", "C15"], confidence: "S", source: "usFrontier",
                detail: "Publish a frontier AI framework and transparency reports; report critical safety incidents within 15 days, or 24 hours if imminent risk." });
        if (f.synthetic && prov)
            add({ id: "ca-caita", jurisdiction: "US", title: "AI content provenance and detection", basis: "CA SB 942 as amended by AB 853", appliesFrom: "2026-08-02",
                controls: ["C11", "C13"], confidence: "S", source: "usCaita",
                detail: "Generative image, video and audio systems with over 1 million monthly users: free detection tool, latent disclosures and an optional visible label. Text is not covered." });
        // Duties of the business that uses ADMT on its consumers, not of the vendor.
        if (f.personal && people && dep) {
            add({ id: "ca-admt", jurisdiction: "US", title: "Automated decision-making technology rights", basis: "CCPA regulations (CPPA)", appliesFrom: "2027-01-01",
                controls: ["C11", "C17", "C12"], confidence: "S", source: "usCppa",
                detail: "Consumers can opt out of ADMT used for significant decisions where it replaces or substantially replaces human judgment." });
            add({ id: "ca-risk", jurisdiction: "US", title: "CCPA risk assessments", basis: "CCPA regulations (CPPA)", appliesFrom: "2027-12-31",
                controls: ["C06", "C08"], confidence: "S", source: "usCppa",
                detail: "Risk assessments for ADMT used in significant decisions, training ADMT on personal information and processing sensitive data." });
        }
        if (employment)
            add({ id: "ca-feha", jurisdiction: "US", title: "Automated-decision systems in employment", basis: "CA FEHA regulations (Civil Rights Council)", appliesFrom: "2025-10-01",
                controls: ["C18", "C10", "C08"], confidence: "S", source: "usFeha",
                detail: "Discrimination through an ADS is unlawful; anti-bias testing counts in defence; keep ADS data for four years. Vendors acting for an employer can be liable." });
    }
    // Colorado
    if (st.has("CO") && coConsequential) {
        if (dep)
            add({ id: "co-admt-deployer", jurisdiction: "US", title: "Notices and human review for consequential decisions", basis: "CO SB 26-189", appliesFrom: "2027-01-01",
                controls: ["C11", "C17", "C12", "C10"], confidence: "S", source: "usColorado",
                detail: "Pre-use notice; adverse-outcome notice within 30 days explaining the decision and the system's role; correction and human reconsideration; three-year records." });
        if (supplies)
            add({ id: "co-admt-developer", jurisdiction: "US", title: "Developer documentation for deployers", basis: "CO SB 26-189", appliesFrom: "2027-01-01",
                controls: ["C10", "C11"], confidence: "S", source: "usColorado",
                detail: "Give deployers intended and harmful uses, training data categories, known limitations and human-review instructions; notify material updates." });
    }
    // Illinois
    if (st.has("IL")) {
        if (employment)
            add({ id: "il-hra", jurisdiction: "US", title: "AI in employment decisions", basis: "IL Human Rights Act (HB 3773)", appliesFrom: "2026-01-01",
                controls: ["C18", "C11"], confidence: "S", source: "usEmployment",
                detail: "Discriminatory effect is a civil rights violation; no zip codes as a proxy; notify employees. Notice rules await IDHR rulemaking." });
        if (f.frontier && prov)
            add({ id: "il-frontier", jurisdiction: "US", title: "Illinois frontier AI safety", basis: "IL SB 315", appliesFrom: "2027-01-01",
                controls: ["C06", "C10", "C11", "C15"], confidence: "S", source: "usFrontier",
                detail: "SB 53-style framework and disclosures; annual independent audit from 1 January 2028." });
    }
    // New York
    if (st.has("NY") && f.frontier && prov)
        add({ id: "ny-raise", jurisdiction: "US", title: "New York frontier AI safety", basis: "NY RAISE Act (as amended)", appliesFrom: "2027-01-01",
            controls: ["C06", "C10", "C11", "C15"], confidence: "S", source: "usRaise",
            detail: "Frontier AI framework, transparency reports, quarterly risk summaries, incident reports within 72 hours, disclosure statement filed with DFS." });
    if (st.has("NYC") && employment)
        add({ id: "nyc-ll144", jurisdiction: "US", title: "Automated employment decision tools", basis: "NYC Local Law 144", appliesFrom: "in-force",
            controls: ["C18", "C11"], confidence: "S", source: "usEmployment",
            detail: "Annual independent bias audit before use, published summary, and candidate notice. Applies to hiring and promotion in New York City." });
    // Connecticut
    if (st.has("CT") && employment && dep)
        add({ id: "ct-aedt", jurisdiction: "US", title: "Employment decision tool notices", basis: "CT SB 5 (2026)", appliesFrom: "2027-10-01",
            controls: ["C11"], confidence: "S", source: "usEmployment",
            detail: "Written notice of the tool, its purpose, the personal data used and its sources, where the output is a substantial factor in an employment decision." });
    // Texas
    if (st.has("TX"))
        add({ id: "tx-traiga", jurisdiction: "US", title: "Texas Responsible AI Governance Act", basis: "TX HB 149 (TRAIGA)", appliesFrom: "2026-01-01",
            controls: ["C06", "C18", "C11"], confidence: "M", source: "usTexas",
            detail: "Bans AI used with intent to discriminate or for specified harmful purposes; disclosure duties for government bodies and healthcare. Sources differ on wider disclosure duties and NIST-based defences: check the enacted text." });
    // Utah
    if (st.has("UT") && (f.chatbot || f.synthetic))
        add({ id: "ut-disclosure", jurisdiction: "US", title: "Generative AI disclosure", basis: "Utah Code ch. 13-77", appliesFrom: "in-force",
            controls: ["C11"], confidence: "S", source: "usUtah",
            detail: "Say it is AI when a consumer asks. Licensed occupations must disclose up front in high-risk interactions involving health, financial or biometric data or personal advice." });
    return out;
}
/** Third-party supply: a bought-in system, or one built on someone else's model, API or data. */
function supplierDependency(s) {
    if (!ROLE_FACETS[s.role].builds)
        return "Bought or licensed from a supplier";
    if (s.flags?.thirdParty)
        return "Built on a third-party model, API or dataset";
    return null;
}
export function applicableControls(s) {
    const ids = new Set(BASE_CONTROLS);
    for (const o of obligations(s))
        for (const c of o.controls)
            ids.add(c);
    if (supplierDependency(s))
        ids.add("C16");
    return CONTROLS.filter((c) => ids.has(c.id));
}
/** For each applicable control, the obligations or profile facts that require it. */
export function controlDrivers(s) {
    const out = {};
    for (const o of obligations(s))
        for (const c of o.controls)
            (out[c] ??= []).push(`${o.jurisdiction} ${o.basis}`);
    const dependency = supplierDependency(s);
    if (dependency)
        (out["C16"] ??= []).push(dependency);
    return out;
}
export function tier(s) {
    if (s.jurisdictions.includes("EU") && (ANNEX_III.includes(s.domain) || s.domain === "product"))
        return { label: "EU high-risk", tone: "crit" };
    if (s.flags?.auGov && s.jurisdictions.includes("AU"))
        return { label: "AU public sector", tone: "warn" };
    if (s.jurisdictions.includes("US") && (["employment", "essential", "education"].includes(s.domain) || s.flags?.healthcare))
        return { label: "US consequential decision", tone: "warn" };
    if (s.flags?.chatbot || s.flags?.synthetic || s.flags?.gpai)
        return { label: "Transparency", tone: "info" };
    return { label: "Baseline", tone: "mute" };
}
/**
 * Coverage over applicable controls, excluding those marked not applicable.
 * In-place counts 1, in-progress counts 0.5.
 */
export function coverage(s, states, onlyIds) {
    const ids = (onlyIds ?? applicableControls(s).map((c) => c.id)).filter((id) => states[id]?.status !== "na");
    if (!ids.length)
        return { pct: 0, done: 0, partial: 0, applicable: 0 };
    const done = ids.filter((id) => states[id]?.status === "done").length;
    const partial = ids.filter((id) => states[id]?.status === "partial").length;
    return { pct: Math.round(((done + partial * 0.5) / ids.length) * 100), done, partial, applicable: ids.length };
}
export function frameworkCoverage(s, states) {
    const ctls = applicableControls(s);
    // Regional frameworks only count where the system is used; ISO and NIST always apply.
    const regional = { EU: "EU", AI6: "AU", UK: "UK" };
    return ["42001", "23894", "42005", "NIST", "EU", "AI6", "UK"]
        .filter((k) => !regional[k] || s.jurisdictions.includes(regional[k]))
        .map((k) => {
        const ids = ctls.filter((c) => c.refs[k]).map((c) => c.id);
        return { framework: k, controls: ids.length, coverage: coverage(s, states, ids) };
    })
        .filter((x) => x.controls > 0);
}
/** The earliest dated obligation strictly after asOf (ISO yyyy-mm-dd). */
export function nextDeadline(s, asOf) {
    const dates = obligations(s)
        .map((o) => o.appliesFrom)
        .filter((d) => !!d && d !== "in-force" && d > asOf)
        .sort();
    return dates[0] ?? null;
}
export function obligationState(o, asOf) {
    if (o.kind)
        return { label: o.kind, tone: o.kind === "Watch" ? "mute" : "info" };
    if (!o.appliesFrom || o.appliesFrom === "in-force" || o.appliesFrom <= asOf)
        return { label: "In force", tone: "crit" };
    const days = Math.round((Date.parse(o.appliesFrom) - Date.parse(asOf)) / 864e5);
    return { label: `Due ${formatDate(o.appliesFrom)} (${days}d)`, tone: days < 120 ? "warn" : "info" };
}
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function formatDate(iso) {
    const [y, m, d] = iso.slice(0, 10).split("-");
    return `${Number(d)} ${MONTHS[Number(m) - 1]} ${y}`;
}
export function isoDate(d = new Date()) {
    return d.toISOString().slice(0, 10);
}
//# sourceMappingURL=engine.js.map